import * as React from "react"
import moment from 'moment'
import { MinimumWageBasicConfigurationModel } from "@/app/models/configuration/minimumWage/minimumWageBasicConfigurationModel"
import { FieldsetSection } from "../components/FieldsetSection";
import { RowCheckbox } from "../components/RowCheckbox";
import { useLocalize } from "@/modules/shared/context/localize/LocalizeContext";
import { RowText } from "../components/RowText";
import { AccommodationFilterOptions } from "@/app/constants/configuration/accommodationFilterOptions";
import { DATE_FORMAT } from "@/app/constants/date";

const defaultFields: FieldName[] = [
    "printCalculatedHours",
    "calculateCabotageAndCrossTrade",
]

export type FieldName =
    | 'increaseRateAccordingToWorkYears'
    | 'includeAdditionForHolidayWork'
    | "includeAdditionForWeekOvertime"
    | "includeAdditionForDayOvertime"
    | "includeSanitaryAddition"
    | "addExtraAditionForCountry"
    | "includeAdditionForNightTimeWork"
    | "includeAllowanceToPayment"
    | "includeAccomodationToPayment"
    | "printCalculatedHours"
    | "calculateCabotageAndCrossTrade"

type Props = {
    className?: string
    loading?: boolean
    fields?: FieldName[]
    translateKey?: string
    state: { configuration: MinimumWageBasicConfigurationModel }
    accommodationAndAllowanceMaxDate: any
    setState: React.Dispatch<React.SetStateAction<{ configuration: MinimumWageBasicConfigurationModel }>>
}
                
export const MinimumWageOptionsSection = ({
    className,
    loading,
    fields = defaultFields,
    translateKey,
    accommodationAndAllowanceMaxDate,
    state,
    setState,
}: Props) => {
    const { t } = useLocalize()

    const updateValue = React.useCallback((name: string, value: any) => {
        setState((s) => ({
            ...s,
            configuration: {
                ...s.configuration,
                [name]: value,
            },
        }))
    }, [setState])

    if (loading) {
        return <p>Loading...</p>
    }

    const basicConfiguration = state.configuration

    const readOnly =
        state != null
            ? !state.configuration.calculateAmountsForCountry
            : false;

    return (
        <div className={className}>
            <FieldsetSection title={t('configuration.minimumWages.options')}>

                {/* increaseRateAccordingToWorkYears */}
                {fields.includes('increaseRateAccordingToWorkYears') && (
                    <RowCheckbox
                        label={t(`configuration.minimumWages.${translateKey}.increaseRateAccordingToWorkYears`)}
                        id='increaseRateAccordingToWorkYears'
                        name='increaseRateAccordingToWorkYears'
                        checked={basicConfiguration.increaseRateAccordingToWorkYears}
                        readOnly={readOnly}
                        setValue={(value) => updateValue('increaseRateAccordingToWorkYears', value)}
                    />
                )}

                {/* includeAdditionForHolidayWork */}
                {fields.includes('includeAdditionForHolidayWork') && (
                    <RowCheckbox
                        label={t(`configuration.minimumWages.${translateKey}.includeAdditionForHolidayWork`)}
                        id='includeAdditionForHolidayWork'
                        name='includeAdditionForHolidayWork'
                        checked={basicConfiguration.includeAdditionForHolidayWork}
                        readOnly={readOnly}
                        setValue={(value) => updateValue('includeAdditionForHolidayWork', value)}
                    />
                )}

                {/* includeAdditionForWeekOvertime */}
                {fields.includes('includeAdditionForWeekOvertime') && (
                    <RowCheckbox
                        label={t(`configuration.minimumWages.${translateKey}.includeAdditionForWeekOvertime`)}
                        id='includeAdditionForWeekOvertime'
                        name='includeAdditionForWeekOvertime'
                        checked={basicConfiguration.includeAdditionForWeekOvertime}
                        readOnly={readOnly}
                        setValue={(value) => updateValue('includeAdditionForWeekOvertime', value)}
                    />
                )}

                {/* includeAdditionForDayOvertime */}
                {fields.includes('includeAdditionForDayOvertime') && (
                    <RowCheckbox
                        label={t(`configuration.minimumWages.${translateKey}.includeAdditionForDayOvertime`)}
                        id='includeAdditionForDayOvertime'
                        name='includeAdditionForDayOvertime'
                        checked={basicConfiguration.includeAdditionForDayOvertime}
                        readOnly={readOnly}
                        setValue={(value) => updateValue('includeAdditionForDayOvertime', value)}
                    />
                )}

                {/* includeSanitaryAddition */}
                {fields.includes('includeSanitaryAddition') && (
                    <RowCheckbox
                        label={t(`configuration.minimumWages.${translateKey}.includeSanitaryAddition`)}
                        id='includeSanitaryAddition'
                        name='includeSanitaryAddition'
                        checked={basicConfiguration.includeSanitaryAddition}
                        readOnly={readOnly}
                        setValue={(value) => updateValue('includeSanitaryAddition', value)}
                    />
                )}

                {/* addExtraAditionForCountry */}
                {fields.includes('addExtraAditionForCountry') && (
                    <RowCheckbox
                        label={t(`configuration.minimumWages.${translateKey}.addExtraAditionForCountry`)}
                        id="addExtraAditionForCountry"
                        name="addExtraAditionForCountry"
                        checked={basicConfiguration?.addExtraAditionForCountry}
                        readOnly={readOnly}
                        setValue={(value) => updateValue('addExtraAditionForCountry', value)}
                    />
                )}

                {/* includeAdditionForNightTimeWork */}
                {fields.includes('includeAdditionForNightTimeWork') && (
                    <RowCheckbox
                        label={t(`configuration.minimumWages.${translateKey}.includeAdditionForNightTimeWork`)}
                        id="includeAdditionForNightTimeWork"
                        name="includeAdditionForNightTimeWork"
                        checked={basicConfiguration?.includeAdditionForNightTimeWork}
                        readOnly={readOnly}
                        setValue={(value) => updateValue('includeAdditionForNightTimeWork', value)}
                    />
                )}

                {/* includeAllowanceToPayment (with date placeholder) */}
                {fields.includes('includeAllowanceToPayment') && (
                    <RowCheckbox
                        label={t(`configuration.minimumWages.${translateKey}.includeAllowanceToPayment`)
                            .replace('{0}', moment(accommodationAndAllowanceMaxDate).format(DATE_FORMAT))}
                        id="includeAllowanceToPayment"
                        name="includeAllowanceToPayment"
                        checked={basicConfiguration?.includeAllowanceToPayment}
                        readOnly={readOnly}
                        setValue={(value) => updateValue('includeAllowanceToPayment', value)}
                    >
                        <RowCheckbox
                            associated
                            label={t(`configuration.minimumWages.${translateKey}.compensateMinimumSubsistenceLevelForAccomodation`)}
                            id="compensateMinimumSubsistenceLevelForFood"
                            name="compensateMinimumSubsistenceLevelForFood"
                            checked={basicConfiguration?.compensateMinimumSubsistenceLevelForFood}
                            readOnly={readOnly}
                            setValue={(value) => updateValue('compensateMinimumSubsistenceLevelForFood', value)}
                        />
                    </RowCheckbox>
                )}

                {/* includeAccomodationToPayment (with date placeholder) */}
                {fields.includes('includeAccomodationToPayment') && (
                    <RowCheckbox
                        label={t(`configuration.minimumWages.${translateKey}.includeAccomodationToPayment`)
                            .replace('{0}', moment(accommodationAndAllowanceMaxDate).format(DATE_FORMAT))}
                        id="includeAccomodationToPayment"
                        name="includeAccomodationToPayment"
                        checked={basicConfiguration?.includeAccomodationToPayment}
                        readOnly={readOnly}
                        setValue={(value) => updateValue('includeAccomodationToPayment', value)}
                    >
                        <>
                            <RowCheckbox
                                associated
                                indeterminate
                                checked={basicConfiguration.accommodationFilterOptions === AccommodationFilterOptions.All}
                                id="bAccommodationFilterOptionsAll"
                                label={t('configuration.minimumWages.accommodationFilterOptionAll')}
                                readOnly={readOnly}
                                setValue={(value) => updateValue('accommodationFilterOptions', AccommodationFilterOptions.All)}
                            />
                            <RowCheckbox
                                associated
                                indeterminate
                                checked={
                                    basicConfiguration.accommodationFilterOptions ===
                                    AccommodationFilterOptions.NotOnValidWeekRests
                                }
                                id="bAccommodationFilterOptionsWithoutValidWeekRests"
                                label={t('configuration.minimumWages.accommodationFilterOptionWithoutLongRests')}
                                readOnly={readOnly}
                                setValue={(value) => updateValue('accommodationFilterOptions', AccommodationFilterOptions.NotOnValidWeekRests)}
                            />
                        </>
                    </RowCheckbox>
                )}

                {/* printCalculatedHours */}
                {fields.includes('printCalculatedHours') && (
                    <RowCheckbox
                        label={t(`configuration.minimumWages.${translateKey}.printCalculatedHours`)}
                        id="printCalculatedHours"
                        name="printCalculatedHours"
                        checked={basicConfiguration?.printCalculatedHours}
                        readOnly={readOnly}
                        setValue={(value) => updateValue('printCalculatedHours', value)}
                    />
                )}

                {/* calculateCabotageAndCrossTrade (title row with associated controls below) */}
                {fields.includes('calculateCabotageAndCrossTrade') && (
                    <RowText
                        /* Header-like row with only label + associated controls, no main checkbox */
                        label={t('configuration.minimumWages.genericConfiguration.calculateCabotageAndCrossTrade')}
                        readOnly={readOnly}
                    >
                        <>
                            <RowCheckbox
                                associated
                                checked={basicConfiguration.includeCabotage}
                                id="cabotage"
                                label={t('configuration.minimumWages.genericConfiguration.cabotage')}
                                readOnly={readOnly}
                                setValue={(value) => updateValue('includeCabotage', !state.configuration.includeCabotage)}
                            />
                            <RowCheckbox
                                associated
                                checked={basicConfiguration.includeCrossTrade}
                                id="crossTrade"
                                label={t('configuration.minimumWages.genericConfiguration.crossTrade')}
                                readOnly={readOnly}
                                setValue={(value) => updateValue('includeCrossTrade', !state.configuration.includeCrossTrade)}
                            />
                        </>
                    </RowText>
                )}
            </FieldsetSection>
        </div>
    )
}
